import { type JSX, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ChevronDown, X as CloseIcon } from 'react-feather';
import styled from '@emotion/styled';
import { useFloating, flip, shift, offset, size, useClick, useDismiss, useInteractions } from '@floating-ui/react';
import { RichTreeView, TreeItem2, type TreeItem2Props } from '@mui/x-tree-view';
import { AppThemingContext } from '../../containers/AppTheming/Context';
import {
  type FilterTreeNode,
  addLeafPaths,
  buildFilterTree,
  getIndeterminateItemIds,
  getSelectedItemIds,
  idToPath,
  removePathAndDescendants,
} from '../../utils/nested-filter';

const IndeterminateIdsContext = createContext<ReadonlySet<string>>(new Set());

function IndeterminateTreeItem(props: TreeItem2Props): JSX.Element {
  const indeterminateIds = useContext(IndeterminateIdsContext);
  return (
    <TreeItem2
      {...props}
      slotProps={{
        ...props.slotProps,
        checkbox: {
          ...props.slotProps?.checkbox,
          // @ts-expect-error -- TreeItem2Checkbox renders a MUI Checkbox at runtime which accepts `indeterminate`, but the slot type is declared as 'button' element props
          indeterminate: indeterminateIds.has(props.itemId),
        },
      }}
    />
  );
}

export type NestedFilterProps = {
  label: string;
  paths: string[][];
  selectedPaths: string[][];
  onSelectionChange: (paths: string[][]) => void;
};

export function NestedFilter({
  label,
  paths,
  selectedPaths,
  onSelectionChange,
}: NestedFilterProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const { palette, enabled: whitelabelEnabled } = useContext(AppThemingContext);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom',
    middleware: [
      offset(2),
      flip(),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context),
  ]);

  const tree = useMemo(() => buildFilterTree(paths), [paths]);
  const isFlat = useMemo(() => tree.every(node => node.children.length === 0), [tree]);

  const selectedItemIds = useMemo(
    () => getSelectedItemIds(tree, paths, selectedPaths),
    [tree, paths, selectedPaths],
  );

  const indeterminateItemIds = useMemo(
    () => new Set(getIndeterminateItemIds(tree, paths, selectedPaths)),
    [tree, paths, selectedPaths],
  );

  const handleItemSelectionToggle = useCallback(
    (_event: React.SyntheticEvent, itemId: string, isSelected: boolean) => {
      const nodePath = idToPath(itemId);
      const newPaths = isSelected
        ? addLeafPaths(selectedPaths, paths, nodePath)
        : removePathAndDescendants(selectedPaths, nodePath);
      onSelectionChange(newPaths);
    },
    [selectedPaths, paths, onSelectionChange],
  );

  const handleRemoveTag = useCallback(
    (pathToRemove: string[], e: React.MouseEvent) => {
      e.stopPropagation();
      onSelectionChange(removePathAndDescendants(selectedPaths, pathToRemove));
    },
    [selectedPaths, onSelectionChange],
  );

  const tags = useMemo(
    () => selectedPaths.map((path, idx) => ({
      id: `tag-${idx}`,
      label: path[path.length - 1] ?? path.join(' > '),
      path,
    })),
    [selectedPaths],
  );

  const hasSelections = selectedPaths.length > 0;

  const primaryColor = whitelabelEnabled && palette?.primary?.main
    ? palette.primary.main
    : 'var(--pri-02)';

  return (
    <Root>
      <FilterLabel>{label}</FilterLabel>
      <Trigger ref={refs.setReference} {...getReferenceProps()}>
        <TriggerContent>
          {hasSelections ? (
            <InlineTags>
              {tags.map(tag => (
                <Tag key={tag.id}>
                  <TagLabel>{tag.label}</TagLabel>
                  <TagRemove onClick={e => handleRemoveTag(tag.path, e)}>
                    <CloseIcon size={10} />
                  </TagRemove>
                </Tag>
              ))}
            </InlineTags>
          ) : (
            <Placeholder>Select a value</Placeholder>
          )}
        </TriggerContent>
        <ChevronWrapper $isOpen={isOpen}>
          <ChevronDown size={14} />
        </ChevronWrapper>
      </Trigger>
      {isOpen && (
        <>
          <FloatingPanel ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
            {tree.length > 0 ? (
              <TreeContainer $primaryColor={primaryColor} $isFlat={isFlat}>
                <IndeterminateIdsContext.Provider value={indeterminateItemIds}>
                  <RichTreeView
                    items={tree}
                    getItemId={(item: FilterTreeNode) => item.id}
                    getItemLabel={(item: FilterTreeNode) => item.label}
                    multiSelect
                    checkboxSelection
                    selectedItems={Array.from(selectedItemIds)}
                    onItemSelectionToggle={handleItemSelectionToggle}
                    isItemDisabled={() => false}
                    slots={{ item: IndeterminateTreeItem }}
                    slotProps={{
                      item: {
                        sx: {
                          '& .MuiTreeItem-label': { fontSize: '13px' },
                          '& .MuiCheckbox-root': {
                            padding: '2px',
                            '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                              color: primaryColor,
                            },
                          },
                        },
                      },
                    }}
                  />
                </IndeterminateIdsContext.Provider>
              </TreeContainer>
            ) : (
              <EmptyMessage>No options available</EmptyMessage>
            )}
          </FloatingPanel>
        </>
      )}
    </Root>
  );
}

// Styled components

const Root = styled.div({
  position: 'relative',
});

const FilterLabel = styled.div({
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--gray-d, #555)',
  marginBottom: 4,
});

const Trigger = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 10px',
  borderRadius: 4,
  border: '1px solid var(--gray-l, #d0d0d0)',
  backgroundColor: '#ffffff',
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    borderColor: 'var(--gray-m, #aaa)',
  },
});

const TriggerContent = styled.div({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 4,
});

const Placeholder = styled.span({
  fontSize: 14,
  color: 'var(--gray-m, #aaa)',
});

const InlineTags = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
});

const Tag = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 8px',
  borderRadius: 12,
  backgroundColor: 'var(--gray-l, #e8e8e8)',
  color: 'var(--gray-dd, #333)',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '22px',
  maxWidth: 220,
});

const TagLabel = styled.span({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const TagRemove = styled.button({
  display: 'flex',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: 'inherit',
  opacity: 0.6,
  flexShrink: 0,
  '&:hover': { opacity: 1 },
});

const ChevronWrapper = styled.div<{ $isOpen: boolean }>(({ $isOpen }) => ({
  display: 'flex',
  alignItems: 'center',
  color: 'var(--gray-m, #999)',
  flexShrink: 0,
  transition: 'transform 0.15s',
  transform: $isOpen ? 'rotate(180deg)' : 'none',
}));

type TreeContainerProps = {
  $primaryColor: string;
  $isFlat: boolean;
};

const TreeContainer = styled.div<TreeContainerProps>(({ $isFlat }) => ({
  maxHeight: 360,
  overflowY: 'auto',
  '& .MuiTreeItem-root': {
    '&.Mui-selected': {
      backgroundColor: 'transparent !important',
    },
    '& .MuiTreeItem-content': {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 3,
      paddingBottom: 3,
      minHeight: 30,
      '&.Mui-selected': {
        backgroundColor: 'transparent !important',
      },
    },
    '& .MuiTreeItem-label': {
      fontSize: 13,
      color: 'var(--gray-d)',
    },
    '& .MuiTreeItem-iconContainer': {
      width: $isFlat ? 0 : 20,
      display: $isFlat ? 'none' : undefined,
    },
  },
  '& .MuiCheckbox-root': {
    padding: '2px',
  },
}));

const FloatingPanel = styled.div({
  backgroundColor: '#ffffff',
  borderRadius: 4,
  border: '1px solid var(--gray-l, #d0d0d0)',
  padding: '6px 8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  zIndex: 1300,
  boxSizing: 'border-box',
});

const EmptyMessage = styled.div({
  padding: '8px 4px',
  fontSize: 13,
  color: 'var(--gray-m)',
  fontStyle: 'italic',
});


